import React, { useState, useEffect, useRef } from 'react';
import { Upload, AlertCircle, Check, Loader2, FileText, Search, ChevronDown, X, CheckCircle2, Circle, XCircle } from 'lucide-react';

// Progress steps definition
const PROGRESS_STEPS = [
  { num: 1, name: 'Creating Brand', description: 'Setting up brand record' },
  { num: 2, name: 'Fetching Backlinks', description: 'Getting existing backlink data' },
  { num: 3, name: 'Comparing Backlinks', description: 'Analyzing CSV vs existing data' },
  { num: 4, name: 'Google Search', description: 'Finding brand mentions via Zyte SERP' },
  { num: 5, name: 'Classification', description: 'Classifying URLs with Claude' },
  { num: 6, name: 'Processing Emails', description: 'Handling email data' },
  { num: 7, name: 'Domain Ranks', description: 'Fetching SEMrush rankings' },
  { num: 8, name: 'Organic Keywords', description: 'Getting organic keyword data' },
  { num: 9, name: 'Paid Keywords', description: 'Getting paid keyword data' },
  { num: 10, name: 'Top Geos', description: 'Fetching geographic data' },
  { num: 11, name: 'Merging Data', description: 'Updating affiliate_clean table' },
  { num: 12, name: 'Finalizing', description: 'Removing lost affiliates' }
];

const UpdateBrand = () => {
  const [formData, setFormData] = useState({
    brandName: '',
    brandUrl: '',
    primaryCountry: '',
    csvFile: null
  });
  const [brandUrls, setBrandUrls] = useState([]);
  const [brandsData, setBrandsData] = useState([]);
  const [filteredBrandUrls, setFilteredBrandUrls] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmedData, setConfirmedData] = useState(null);

  // Progress tracking state
  const [showProgress, setShowProgress] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [stepStatuses, setStepStatuses] = useState({});
  const [progressData, setProgressData] = useState({});
  const [progressError, setProgressError] = useState(null);
  const [finalSummary, setFinalSummary] = useState(null);

  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const eventSourceRef = useRef(null);

  // Fetch brands on component mount
  useEffect(() => {
    fetchBrands();

    // Cleanup EventSource on unmount
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // Filter brand URLs based on search query
  useEffect(() => {
    if (searchQuery === '') {
      setFilteredBrandUrls(brandUrls);
    } else {
      const filtered = brandUrls.filter(url =>
        url.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredBrandUrls(filtered);
    }
  }, [searchQuery, brandUrls]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const fetchBrands = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/api/fetchBrand`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setBrandUrls(result.data);
          setFilteredBrandUrls(result.data);
          if (result.brands) {
            setBrandsData(result.brands);
          }
        }
      } else {
        console.error('Failed to fetch brand URLs');
      }
    } catch (error) {
      console.error('Error fetching brand URLs:', error);
    } finally {
      setLoadingBrands(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      // Validate file type
      if (!file.name.toLowerCase().endsWith('.csv')) {
        setErrors(prev => ({
          ...prev,
          csvFile: 'Please select a valid CSV file'
        }));
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          csvFile: 'File size should be less than 5MB'
        }));
        return;
      }

      setFormData(prev => ({
        ...prev,
        csvFile: file
      }));

      // Clear error
      if (errors.csvFile) {
        setErrors(prev => ({
          ...prev,
          csvFile: ''
        }));
      }
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Update brandUrl in formData only if the value matches exactly with one of the brand URLs
    const exactMatch = brandUrls.find(url => url === value);
    if (exactMatch) {
      setFormData(prev => ({
        ...prev,
        brandUrl: value
      }));
    } else {
      // Clear brandUrl if search doesn't match exactly
      setFormData(prev => ({
        ...prev,
        brandUrl: ''
      }));
    }
  };

  const handleSelectBrand = (url) => {
    // Find brand data to auto-fill country
    const brand = brandsData.find(b => b.brand_url === url);
    const country = brand?.country ? brand.country.split(',')[0].trim().toUpperCase() : '';

    setFormData(prev => ({
      ...prev,
      brandUrl: url,
      primaryCountry: country
    }));
    setSearchQuery(url);
    setIsDropdownOpen(false);

    // Clear error
    if (errors.brandUrl) {
      setErrors(prev => ({
        ...prev,
        brandUrl: ''
      }));
    }
  };

  const handleDropdownToggle = () => {
    if (!loadingBrands) {
      setIsDropdownOpen(!isDropdownOpen);
      if (!isDropdownOpen) {
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 100);
      }
    }
  };

  const clearSelection = () => {
    setFormData(prev => ({
      ...prev,
      brandUrl: ''
    }));
    setSearchQuery('');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.brandName.trim()) {
      newErrors.brandName = 'Please enter a brand name';
    }

    if (!formData.brandUrl) {
      newErrors.brandUrl = 'Please select a brand URL';
    }

    if (!formData.csvFile) {
      newErrors.csvFile = 'Please select a CSV file';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Store confirmed data and show confirmation
    setConfirmedData({
      brandName: formData.brandName.trim(),
      brandUrl: formData.brandUrl,
      primaryCountry: formData.primaryCountry,
      csvFile: formData.csvFile
    });
    setShowConfirmation(true);
    setMessage('');
    setSuccess(false);
  };

  const resetProgress = () => {
    setShowProgress(false);
    setCurrentStep(0);
    setStepStatuses({});
    setProgressData({});
    setProgressError(null);
    setFinalSummary(null);
  };

  const handleConfirmSubmit = async () => {
    if (!confirmedData) return;

    setLoading(true);
    setSuccess(false);
    setMessage('');
    setShowConfirmation(false);

    // Reset and show progress
    resetProgress();
    setShowProgress(true);

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';

      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('brand_name', confirmedData.brandName);
      formDataToSend.append('brand_url', confirmedData.brandUrl);
      formDataToSend.append('primary_country', confirmedData.primaryCountry || '');
      formDataToSend.append('csv', confirmedData.csvFile);

      // Use fetch with streaming for SSE
      const response = await fetch(`${apiUrl}/api/updateBrand`, {
        method: 'POST',
        credentials: 'include',
        body: formDataToSend
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to start update process');
      }

      // Handle SSE stream with heartbeat support
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let lastActivity = Date.now();

      // Set up activity timeout (20 minutes of no data = assume connection lost)
      const activityCheckInterval = setInterval(() => {
        const timeSinceLastActivity = Date.now() - lastActivity;
        if (timeSinceLastActivity > 1200000) { // 20 minutes
          console.warn('No activity for 20 minutes - connection may have been lost');
          clearInterval(activityCheckInterval);
        }
      }, 30000); // Check every 30 seconds

      try {
        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            clearInterval(activityCheckInterval);
            break;
          }

          lastActivity = Date.now(); // Update last activity time
          buffer += decoder.decode(value, { stream: true });

          // Process complete SSE messages
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            // Ignore heartbeat messages (start with :)
            if (line.startsWith(':')) {
              continue;
            }

            if (line.startsWith('data: ')) {
              try {
                const data = JSON.parse(line.slice(6));
                handleProgressUpdate(data);
              } catch (parseError) {
                console.error('Error parsing SSE data:', parseError);
              }
            }
          }
        }
      } finally {
        clearInterval(activityCheckInterval);
      }

    } catch (error) {
      console.error('Error updating brand:', error);
      setProgressError(error.message);
      setSuccess(false);
      setMessage(error.message || 'An error occurred while updating the brand');
    } finally {
      setLoading(false);
    }
  };

  const handleProgressUpdate = (data) => {

    const { step, status, message, data: stepData } = data;

    if (status === 'connected') {
      // Initial connection
      setProgressData(prev => ({ ...prev, ...stepData }));
      return;
    }

    if (status === 'error') {
      setProgressError(message);
      return;
    }

    if (status === 'success') {
      // Final success
      setFinalSummary(stepData?.summary);
      setSuccess(true);
      setMessage('Brand updated successfully!');

      // Reset form
      setFormData({
        brandName: '',
        brandUrl: '',
        csvFile: null
      });
      setSearchQuery('');
      setConfirmedData(null);

      // Reset file input
      const fileInput = document.getElementById('csvFile');
      if (fileInput) {
        fileInput.value = '';
      }
      return;
    }

    // Update step status
    setCurrentStep(step);
    setStepStatuses(prev => ({
      ...prev,
      [step]: status
    }));

    // Store step data
    if (stepData) {
      setProgressData(prev => ({
        ...prev,
        [`step${step}`]: stepData
      }));
    }
  };

  const handleCancelConfirm = () => {
    setShowConfirmation(false);
    setConfirmedData(null);
    setMessage('');
  };

  const getStepIcon = (stepNum) => {
    const status = stepStatuses[stepNum];

    if (status === 'completed') {
      return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    }
    if (status === 'in_progress') {
      return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />;
    }
    if (status === 'failed') {
      return <XCircle className="w-5 h-5 text-red-500" />;
    }
    return <Circle className="w-5 h-5 text-gray-300 dark:text-slate-600" />;
  };

  const getStepClass = (stepNum) => {
    const status = stepStatuses[stepNum];

    if (status === 'completed') {
      return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
    }
    if (status === 'in_progress') {
      return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
    }
    if (status === 'failed') {
      return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
    }
    return 'bg-gray-50 dark:bg-slate-800 border-gray-200 dark:border-slate-700';
  };

  // Calculate progress percentage
  const progressPercentage = Math.round((Object.values(stepStatuses).filter(s => s === 'completed').length / 12) * 100);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-950 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-slate-100 mb-2">Update Brand</h1>
          <p className="text-gray-600 dark:text-slate-400">Enter brand details and upload CSV file to update brand data</p>
        </div>

        {/* Progress Panel */}
        {showProgress && (
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">
                Update Progress
              </h2>
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                {progressPercentage}% Complete
              </span>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2.5 mb-6">
              <div
                className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>

            {/* Error Display */}
            {progressError && (
              <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <div className="flex items-center gap-2 text-red-700 dark:text-red-400">
                  <XCircle className="w-5 h-5" />
                  <span className="font-medium">Error:</span>
                  <span>{progressError}</span>
                </div>
              </div>
            )}

            {/* Final Summary - User Friendly */}
            {finalSummary && (
              <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <h3 className="font-semibold text-green-800 dark:text-green-300 mb-4 text-lg">Update Complete!</h3>

                {/* Main Stats Cards */}
                <div className="grid grid-cols-2 gap-3 mb-4">
                  <div className="bg-green-100 dark:bg-green-800/30 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-green-700 dark:text-green-300">
                      {finalSummary.classification?.affiliatesFound || 0}
                    </div>
                    <div className="text-sm text-green-600 dark:text-green-400 mt-1">Affiliates Found</div>
                  </div>
                  <div className="bg-blue-100 dark:bg-blue-800/30 p-4 rounded-lg text-center">
                    <div className="text-3xl font-bold text-blue-700 dark:text-blue-300">
                      {finalSummary.classification?.totalProcessed || 0}
                    </div>
                    <div className="text-sm text-blue-600 dark:text-blue-400 mt-1">Domains Analyzed</div>
                  </div>
                </div>

                {/* Backlinks Changes */}
                {finalSummary.csvComparison && (
                  <div className="mb-4 p-3 bg-white dark:bg-slate-800 rounded-lg border border-green-200 dark:border-green-700">
                    <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2 text-sm">Changes</h4>
                    <div className="flex justify-around text-center">
                      <div>
                        <div className="text-xl font-bold text-green-600 dark:text-green-400">
                          +{(finalSummary.csvComparison.gained || 0) + (finalSummary.googleSearch?.domainsInserted || 0)}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">New</div>
                      </div>
                      <div className="border-l border-gray-200 dark:border-gray-600"></div>
                      <div>
                        <div className="text-xl font-bold text-red-600 dark:text-red-400">-{finalSummary.csvComparison.lost || 0}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Lost</div>
                      </div>
                      <div className="border-l border-gray-200 dark:border-gray-600"></div>
                      <div>
                        <div className="text-xl font-bold text-gray-600 dark:text-gray-400">{finalSummary.csvComparison.stable || 0}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Unchanged</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Additional Info */}
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {finalSummary.classification?.serpVerified > 0 && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      <span>{finalSummary.classification.serpVerified} verified mentions</span>
                    </div>
                  )}
                  {finalSummary.finalResults && (
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                      <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                      <span>{finalSummary.finalResults.totalMerged || 0} total in database</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Steps List */}
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {PROGRESS_STEPS.map((step) => (
                <div
                  key={step.num}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all ${getStepClass(step.num)}`}
                >
                  {getStepIcon(step.num)}
                  <div className="flex-1">
                    <div className="font-medium text-gray-900 dark:text-slate-100 text-sm">
                      {step.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-slate-400">
                      {step.description}
                    </div>
                  </div>
                  {stepStatuses[step.num] === 'in_progress' && (
                    <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                      Processing...
                    </span>
                  )}
                  {stepStatuses[step.num] === 'completed' && progressData[`step${step.num}`] && (
                    <span className="text-xs text-green-600 dark:text-green-400">
                      {Object.entries(progressData[`step${step.num}`])
                        .filter(([key]) => !['brandId', 'brandUrl', 'brandName', 'tableName'].includes(key))
                        .map(([key, value]) => `${key}: ${value}`)
                        .join(', ')
                      }
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Close button when complete or error */}
            {(success || progressError) && (
              <button
                onClick={resetProgress}
                className="mt-4 w-full py-2 px-4 bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-lg transition-colors"
              >
                Close Progress
              </button>
            )}
          </div>
        )}

        {/* Form Card - Hide when progress is showing and not complete */}
        {(!showProgress || success || progressError) && (
          <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-8">
            {/* Success/Error Message */}
            {message && !showProgress && (
              <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
                success
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400'
              }`}>
                {success ? (
                  <Check className="w-5 h-5 flex-shrink-0" />
                ) : (
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                )}
                <span>{message}</span>
              </div>
            )}

            {/* Confirmation Dialog */}
            {showConfirmation && confirmedData && (
              <div className="mb-6 p-6 border-2 border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                <div className="flex items-center gap-3 mb-4">
                  <AlertCircle className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">Confirm Submission</h3>
                </div>

                <div className="space-y-3 mb-6 text-sm">
                  <div className="bg-white dark:bg-slate-800 p-3 rounded border dark:border-slate-700">
                    <span className="font-medium text-gray-700 dark:text-slate-400">Brand Name:</span>
                    <span className="ml-2 text-gray-900 dark:text-slate-100">{confirmedData.brandName}</span>
                  </div>
                  <div className="bg-white dark:bg-slate-800 p-3 rounded border dark:border-slate-700">
                    <span className="font-medium text-gray-700 dark:text-slate-400">Brand URL:</span>
                    <span className="ml-2 text-gray-900 dark:text-slate-100 break-all">{confirmedData.brandUrl}</span>
                  </div>
                  <div className="bg-white dark:bg-slate-800 p-3 rounded border dark:border-slate-700">
                    <span className="font-medium text-gray-700 dark:text-slate-400">CSV File:</span>
                    <span className="ml-2 text-gray-900 dark:text-slate-100">{confirmedData.csvFile.name}</span>
                    <span className="ml-2 text-gray-500 dark:text-slate-400">({(confirmedData.csvFile.size / 1024).toFixed(1)} KB)</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={handleConfirmSubmit}
                    disabled={loading}
                    className={`flex-1 py-3 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                      loading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-green-600 hover:bg-green-700 focus:ring-4 focus:ring-green-500/20'
                    } text-white`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Starting...
                      </>
                    ) : (
                      <>
                        <Check className="w-5 h-5" />
                        Confirm & Submit
                      </>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={handleCancelConfirm}
                    disabled={loading}
                    className="px-6 py-3 rounded-lg font-medium border-2 border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-800 focus:ring-4 focus:ring-gray-500/20 transition-all duration-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <div className="space-y-6">
              {/* Brand Name Input */}
              <div>
                <label htmlFor="brandName" className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">
                  Brand Name *
                </label>
                <input
                  type="text"
                  id="brandName"
                  name="brandName"
                  value={formData.brandName}
                  onChange={handleInputChange}
                  placeholder="Enter brand name..."
                  disabled={loading}
                  className={`w-full px-4 py-3 border rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:bg-slate-900 dark:text-slate-100 ${
                    errors.brandName
                      ? 'border-red-300 dark:border-red-800 focus:border-red-500'
                      : 'border-gray-300 dark:border-slate-700 focus:border-blue-500 hover:border-gray-400 dark:hover:border-slate-600'
                  } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                />
                {errors.brandName && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.brandName}
                  </p>
                )}
              </div>

              {/* Searchable Brand URL Select */}
              <div>
                <label htmlFor="brandUrl" className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">
                  Select Brand URL *
                </label>
                {loadingBrands ? (
                  <div className="flex items-center gap-2 p-3 border rounded-lg bg-gray-50 dark:bg-slate-800 dark:border-slate-700">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-gray-500 dark:text-slate-400">Loading brand URLs...</span>
                  </div>
                ) : (
                  <div className="relative" ref={dropdownRef}>
                    {/* Search Input */}
                    <div className={`relative flex items-center border rounded-lg transition-colors dark:bg-slate-900 ${
                      errors.brandUrl ? 'border-red-300 dark:border-red-800' : 'border-gray-300 dark:border-slate-700 hover:border-gray-400 dark:hover:border-slate-600 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20'
                    } ${loading ? 'opacity-50' : ''}`}>
                      <div className="flex items-center pl-3">
                        <Search className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                      </div>
                      <input
                        ref={searchInputRef}
                        type="text"
                        placeholder="Search and select brand URL..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        onFocus={() => !loading && setIsDropdownOpen(true)}
                        disabled={loading}
                        className="w-full px-3 py-3 focus:outline-none dark:bg-slate-900 dark:text-slate-100 disabled:cursor-not-allowed"
                      />
                      <div className="flex items-center pr-3 gap-1">
                        {formData.brandUrl && !loading && (
                          <button
                            type="button"
                            onClick={clearSelection}
                            className="p-1 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors"
                          >
                            <X className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={handleDropdownToggle}
                          disabled={loading}
                          className="p-1 hover:bg-gray-100 dark:hover:bg-slate-800 rounded transition-colors disabled:cursor-not-allowed"
                        >
                          <ChevronDown className={`w-4 h-4 text-gray-400 dark:text-slate-500 transition-transform ${
                            isDropdownOpen ? 'rotate-180' : ''
                          }`} />
                        </button>
                      </div>
                    </div>

                    {/* Dropdown */}
                    {isDropdownOpen && !loading && (
                      <div className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredBrandUrls.length > 0 ? (
                          filteredBrandUrls.map((url, index) => (
                            <button
                              key={index}
                              type="button"
                              onClick={() => handleSelectBrand(url)}
                              className={`w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors border-b border-gray-100 dark:border-slate-700 last:border-b-0 ${
                                formData.brandUrl === url ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' : 'text-gray-900 dark:text-slate-100'
                              }`}
                            >
                              <div className="truncate">{url}</div>
                            </button>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-gray-500 dark:text-slate-400 text-center">
                            No brand URLs found matching "{searchQuery}"
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {errors.brandUrl && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.brandUrl}
                  </p>
                )}
              </div>

              {/* Primary Country */}
              <div>
                <label htmlFor="primaryCountry" className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">
                  Primary Country
                </label>
                <select
                  id="primaryCountry"
                  name="primaryCountry"
                  value={formData.primaryCountry}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="">Auto (from brand)</option>
                  <option value="GB">United Kingdom (GB)</option>
                  <option value="US">United States (US)</option>
                  <option value="DE">Germany (DE)</option>
                  <option value="NL">Netherlands (NL)</option>
                  <option value="SE">Sweden (SE)</option>
                  <option value="NO">Norway (NO)</option>
                  <option value="DK">Denmark (DK)</option>
                  <option value="FI">Finland (FI)</option>
                  <option value="AU">Australia (AU)</option>
                  <option value="CA">Canada (CA)</option>
                  <option value="IE">Ireland (IE)</option>
                  <option value="ES">Spain (ES)</option>
                  <option value="IT">Italy (IT)</option>
                  <option value="FR">France (FR)</option>
                  <option value="AT">Austria (AT)</option>
                  <option value="CH">Switzerland (CH)</option>
                  <option value="BE">Belgium (BE)</option>
                  <option value="PT">Portugal (PT)</option>
                  <option value="PL">Poland (PL)</option>
                  <option value="CZ">Czech Republic (CZ)</option>
                  <option value="NZ">New Zealand (NZ)</option>
                  <option value="IN">India (IN)</option>
                  <option value="BR">Brazil (BR)</option>
                  <option value="ZA">South Africa (ZA)</option>
                  <option value="MT">Malta (MT)</option>
                  <option value="TR">Turkey (TR)</option>
                  <option value="RU">Russia (RU)</option>
                </select>
                <p className="mt-1 text-xs text-gray-500 dark:text-slate-500">Auto-filled from brand. Change if needed for SERP geo targeting.</p>
              </div>

              {/* CSV File Upload */}
              <div>
                <label htmlFor="csvFile" className="block text-sm font-medium text-gray-700 dark:text-slate-400 mb-2">
                  Upload CSV File *
                </label>
                <div className={`relative border-2 border-dashed rounded-lg p-6 transition-colors ${
                  errors.csvFile
                    ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
                    : formData.csvFile
                      ? 'border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                      : 'border-gray-300 dark:border-slate-700 hover:border-gray-400 dark:hover:border-slate-600'
                } ${loading ? 'opacity-50' : ''}`}>
                  <input
                    type="file"
                    id="csvFile"
                    accept=".csv"
                    onChange={handleFileChange}
                    disabled={loading}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
                  />
                  <div className="text-center">
                    {formData.csvFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <FileText className="w-8 h-8 text-green-600 dark:text-green-400" />
                        <div>
                          <p className="text-sm font-medium text-green-700 dark:text-green-400">{formData.csvFile.name}</p>
                          <p className="text-xs text-green-600 dark:text-green-500">
                            {(formData.csvFile.size / 1024).toFixed(1)} KB
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <Upload className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-3" />
                        <p className="text-sm font-medium text-gray-700 dark:text-slate-300">Click to upload CSV file</p>
                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">Maximum file size: 5MB</p>
                      </div>
                    )}
                  </div>
                </div>
                {errors.csvFile && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.csvFile}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <div className="pt-4">
                {!showConfirmation ? (
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={loading}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
                      loading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700 focus:ring-4 focus:ring-blue-500/20'
                    } text-white`}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      'Review & Continue'
                    )}
                  </button>
                ) : (
                  <div className="text-center text-gray-500 dark:text-slate-400">
                    <p>Please review your data above and click "Confirm & Submit" to proceed.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Info Card */}
        {!showProgress && (
          <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <p className="font-medium mb-1">Form Requirements:</p>
                <ul className="list-disc list-inside space-y-0.5 text-blue-600 dark:text-blue-400">
                  <li>Brand name is required</li>
                  <li>Select a valid brand URL from the list</li>
                  <li>CSV file must be in .csv format</li>
                  <li>Maximum file size: 5MB</li>
                  <li>Ensure proper data formatting in CSV</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateBrand;
